/**
 * OsCountSessionDetail — MADE-293
 *
 * Reconciliation table for a single count session.
 *
 * Features:
 *   • Delta colour coding: ✅ green (exact), 🟡 amber (|deltaPct| ≤ 15), 🔴 red (>15), ⬜ grey (uncounted)
 *   • Approve: confirm dialog ("Adjust stock from X → Y?") → POST approve → inline row update
 *   • Reject: bottom-sheet Drawer with optional reason → POST reject → "✗ Rejected" chip
 *   • History: opens OsItemHistoryDrawer for the item's full event timeline
 *   • Compare mode: toggle visible when allSessions has 2+ entries (including current);
 *     shows one column per other session with that session's counted qty
 *   • Close session: dialog warns about uncounted items, fires POST /close
 *   • Polls items every 30 s to show live progress from counting devices
 *   • deltaPct column hidden on small screens via sx display responsive value
 *
 * Routes depend on:
 *   GET  /api/os/audit/sessions/:id/items          — item list + delta, polls 30s
 *   POST /api/os/audit/sessions/:id/items/:num/approve
 *   POST /api/os/audit/sessions/:id/items/:num/reject
 *   POST /api/os/audit/sessions/:id/close
 *   GET  /api/os/audit/sessions/:id/items/:num/history  (delegated to OsItemHistoryDrawer)
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HistoryIcon from '@mui/icons-material/History';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { authFetch } from '../../madebb/utils/authFetch';
import { useTenant } from '../../contexts/TenantContext';
import { OsItemHistoryDrawer } from './OsItemHistoryDrawer';
import type { CountSession } from './OsCountSessionList';

// ── Types ──────────────────────────────────────────────────────────────────────

export interface SessionItem {
  itemNumber: string;
  description: string;
  location: string;
  countedQty: number | null;
  systemQty: number;
  delta: number | null;
  deltaPct: number | null;
  status: 'pending' | 'approved' | 'rejected';
  counted: boolean;
  countedAt: string | null;
}

type ToastState = { message: string; severity: 'success' | 'error' } | null;

// ── Delta helpers ──────────────────────────────────────────────────────────────

type DeltaLevel = 'exact' | 'amber' | 'red' | 'uncounted';

export function getDeltaLevel(item: SessionItem): DeltaLevel {
  if (!item.counted) return 'uncounted';
  if (item.delta === 0) return 'exact';
  const absPct = Math.abs(item.deltaPct ?? 0);
  if (absPct <= 15) return 'amber';
  return 'red';
}

const DELTA_ICON: Record<DeltaLevel, string> = {
  exact: '✅',
  amber: '🟡',
  red: '🔴',
  uncounted: '⬜',
};

const DELTA_COLOR: Record<DeltaLevel, string> = {
  exact: 'success.main',
  amber: 'warning.main',
  red: 'error.main',
  uncounted: 'text.disabled',
};

/**
 * Sort order: pending items with largest |deltaPct| first,
 * then other statuses (approved/rejected), then uncounted at bottom.
 */
function sortItems(items: SessionItem[]): SessionItem[] {
  return [...items].sort((a, b) => {
    // Uncounted always at bottom
    if (!a.counted && b.counted) return 1;
    if (a.counted && !b.counted) return -1;

    // Pending before resolved (approved/rejected)
    const aIsPending = a.status === 'pending';
    const bIsPending = b.status === 'pending';
    if (aIsPending && !bIsPending) return -1;
    if (!aIsPending && bIsPending) return 1;

    // Within pending: largest |deltaPct| first
    if (aIsPending && bIsPending) {
      const aPct = Math.abs(a.deltaPct ?? 0);
      const bPct = Math.abs(b.deltaPct ?? 0);
      return bPct - aPct;
    }

    return 0;
  });
}

// ── Props ──────────────────────────────────────────────────────────────────────

interface Props {
  session: CountSession;
  /** Full list of sessions — used for compare mode column headers. */
  allSessions: CountSession[];
  onBack: () => void;
  onSessionClosed: () => void;
}

// ── Component ──────────────────────────────────────────────────────────────────

export const OsCountSessionDetail: React.FC<Props> = ({
  session,
  allSessions,
  onBack,
  onSessionClosed,
}) => {
  const { apiBase } = useTenant();

  // ── Items state ────────────────────────────────────────────────────────────
  const [items, setItems] = useState<SessionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [updatedTotalItems, setUpdatedTotalItems] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => setUpdatedTotalItems(null), [session.sessionId]);

  // ── Compare mode ──────────────────────────────────────────────────────────
  const [compareMode, setCompareMode] = useState(false);
  /** Map: sessionId → items for that session */
  const [compareItems, setCompareItems] = useState<Map<string, SessionItem[]>>(new Map());
  const [compareLoading, setCompareLoading] = useState(false);

  // Other sessions (excluding the one currently being reviewed)
  // #9a: Compare within the same cycle (when cycleId available), else 48-hour proximity.
  // This prevents historical sessions from other weeks/cycles appearing in reconciliation.
  const otherSessions = useMemo(
    () => {
      if (session.cycleId) {
        return allSessions.filter(
          (s) => s.sessionId !== session.sessionId && s.cycleId === session.cycleId,
        );
      }
      // Fallback for sessions without a cycle association
      const WINDOW_MS = 48 * 60 * 60 * 1000;
      const sessionStart = new Date(session.startedAt).getTime();
      return allSessions.filter(
        (s) =>
          s.sessionId !== session.sessionId &&
          Math.abs(new Date(s.startedAt).getTime() - sessionStart) < WINDOW_MS,
      );
    },
    [allSessions, session.sessionId, session.startedAt, session.cycleId],
  );

  // ── Approve dialog ─────────────────────────────────────────────────────────
  const [approveItem, setApproveItem] = useState<SessionItem | null>(null);
  const [approving, setApproving] = useState(false);
  const [bulkApproving, setBulkApproving] = useState(false);

  // #9b: threshold for auto-approve — flag if |delta| >= max(5, systemQty * 0.10)
  const needsReview = useCallback((item: SessionItem, allCompareItems: Map<string, SessionItem[]>): boolean => {
    const delta = Math.abs((item.countedQty ?? 0) - (parseFloat(String(item.systemQty)) || 0));
    const threshold = Math.max(5, (parseFloat(String(item.systemQty)) || 0) * 0.10);
    if (delta >= threshold) return true;
    // Also flag if any compared session disagrees by more than 1 unit
    if (allCompareItems.size > 0) {
      for (const [, others] of allCompareItems) {
        const match = others.find(o => o.itemNumber === item.itemNumber);
        if (match && Math.abs((match.countedQty ?? 0) - (item.countedQty ?? 0)) > 1) return true;
      }
    }
    return false;
  }, []);

  // ── Reject drawer ──────────────────────────────────────────────────────────
  const [rejectItem, setRejectItem] = useState<SessionItem | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejecting, setRejecting] = useState(false);

  // ── History drawer ─────────────────────────────────────────────────────────
  const [historyItem, setHistoryItem] = useState<SessionItem | null>(null);

  // ── Close session dialog ───────────────────────────────────────────────────
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [closing, setClosing] = useState(false);

  // ── Toast ──────────────────────────────────────────────────────────────────
  const [toast, setToast] = useState<ToastState>(null);

  // ── Data fetching ──────────────────────────────────────────────────────────

  const fetchItems = useCallback(async () => {
    try {
      const res = await authFetch(
        `${apiBase}/audit/sessions/${encodeURIComponent(session.sessionId)}/items`,
      );
      if (!res.ok) throw new Error(`Failed to load items (${res.status})`);
      const data = await res.json();
      setItems(data.items ?? []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load items');
    } finally {
      setLoading(false);
    }
  }, [apiBase, session.sessionId]);

  useEffect(() => {
    fetchItems();
    const id = setInterval(fetchItems, 30_000);
    return () => clearInterval(id);
  }, [fetchItems]);

  // #9: Bulk approve — declared after fetchItems to avoid temporal dead zone
  const handleBulkApprove = useCallback(async (mode: 'no-change' | 'agreed') => {
    setBulkApproving(true);
    const targets = items.filter(item => {
      if (item.status !== 'pending' || item.countedQty == null) return false;
      const delta = Math.abs((item.countedQty ?? 0) - (parseFloat(String(item.systemQty)) || 0));
      if (mode === 'no-change') return delta === 0;
      return !needsReview(item, compareItems);
    });
    let approved = 0, failed = 0;
    for (const item of targets) {
      try {
        const res = await authFetch(
          `${apiBase}/audit/sessions/${session.sessionId}/items/${item.itemNumber}/approve`,
          { method: 'POST' }
        );
        if (res.ok) approved++; else failed++;
      } catch { failed++; }
    }
    await fetchItems();
    setBulkApproving(false);
    if (failed > 0) setToast({ message: `Approved ${approved}, ${failed} failed`, severity: 'error' });
    else setToast({ message: `Approved ${approved} item${approved !== 1 ? 's' : ''}`, severity: 'success' });
  }, [items, compareItems, session.sessionId, apiBase, needsReview, fetchItems]);

  // Load compare-session items when compare mode is toggled on
  useEffect(() => {
    if (!compareMode || otherSessions.length === 0) return;
    setCompareLoading(true);
    Promise.all(
      otherSessions.map(async (s) => {
        try {
          const res = await authFetch(
            `${apiBase}/audit/sessions/${encodeURIComponent(s.sessionId)}/items`,
          );
          if (!res.ok) return [s.sessionId, []] as [string, SessionItem[]];
          const data = await res.json();
          return [s.sessionId, data.items ?? []] as [string, SessionItem[]];
        } catch {
          return [s.sessionId, []] as [string, SessionItem[]];
        }
      }),
    )
      .then((results) => setCompareItems(new Map(results)))
      .catch(console.error)
      .finally(() => setCompareLoading(false));
  }, [compareMode, otherSessions, apiBase]);

  // ── Action handlers ────────────────────────────────────────────────────────

  const handleApprove = async () => {
    if (!approveItem) return;
    setApproving(true);
    try {
      const res = await authFetch(
        `${apiBase}/audit/sessions/${encodeURIComponent(session.sessionId)}/items/${encodeURIComponent(approveItem.itemNumber)}/approve`,
        { method: 'POST' },
      );
      if (!res.ok) {
        const errData = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(errData.error ?? `Approve failed (${res.status})`);
      }
      // Inline row update — no full refetch needed
      setItems((prev) =>
        prev.map((it) =>
          it.itemNumber === approveItem.itemNumber ? { ...it, status: 'approved' } : it,
        ),
      );
      setToast({ message: `${approveItem.description} approved.`, severity: 'success' });
      setApproveItem(null);
    } catch (err) {
      setToast({
        message: err instanceof Error ? err.message : 'Approve failed',
        severity: 'error',
      });
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    if (!rejectItem) return;
    setRejecting(true);
    try {
      const res = await authFetch(
        `${apiBase}/audit/sessions/${encodeURIComponent(session.sessionId)}/items/${encodeURIComponent(rejectItem.itemNumber)}/reject`,
        {
          method: 'POST',
          body: JSON.stringify({ reason: rejectReason.trim() || undefined }),
        },
      );
      if (!res.ok) {
        const errData = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(errData.error ?? `Reject failed (${res.status})`);
      }
      setItems((prev) =>
        prev.map((it) =>
          it.itemNumber === rejectItem.itemNumber ? { ...it, status: 'rejected' } : it,
        ),
      );
      setToast({ message: `${rejectItem.description} rejected.`, severity: 'success' });
      setRejectItem(null);
      setRejectReason('');
    } catch (err) {
      setToast({
        message: err instanceof Error ? err.message : 'Reject failed',
        severity: 'error',
      });
    } finally {
      setRejecting(false);
    }
  };

  const handleCloseSession = async () => {
    const skippedItems = items.filter((it) => !it.counted).map((it) => it.itemNumber);
    setClosing(true);
    try {
      const res = await authFetch(
        `${apiBase}/audit/sessions/${encodeURIComponent(session.sessionId)}/close`,
        { method: 'POST', body: JSON.stringify({ skippedItems }) },
      );
      if (!res.ok) {
        const errData = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(errData.error ?? `Close failed (${res.status})`);
      }
      setCloseDialogOpen(false);
      onSessionClosed();
    } catch (err) {
      setToast({
        message: err instanceof Error ? err.message : 'Failed to close session',
        severity: 'error',
      });
    } finally {
      setClosing(false);
    }
  };

  // ── Derived values ─────────────────────────────────────────────────────────

  const sortedItems = useMemo(() => sortItems(items), [items]);
  const uncountedItems = useMemo(() => items.filter((it) => !it.counted), [items]);
  const hasOtherSessions = otherSessions.length > 0;

  // ── Loading / error ────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress data-testid="session-detail-loading" />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" data-testid="session-detail-error">{error}</Alert>;
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box data-testid="session-detail">
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        spacing={1}
        mb={2}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <IconButton
            onClick={onBack}
            aria-label="Back to sessions"
            data-testid="back-to-sessions"
          >
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h6" fontWeight={700}>
              {session.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {session.countedItems}/{updatedTotalItems ?? session.totalItems} counted ·{' '}
              {session.pendingItems} pending
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
{/* Compare mode toggle — only visible when other sessions exist */}
          {hasOtherSessions && (
            <Box data-testid="compare-toggle-container">
              <Button
                variant={compareMode ? 'contained' : 'outlined'}
                size="small"
                color="secondary"
                onClick={() => setCompareMode((prev) => !prev)}
                data-testid="compare-toggle"
                aria-pressed={compareMode}
              >
                {compareMode ? 'Hide comparison' : 'Compare sessions'}
              </Button>
            </Box>
          )}

          {/* #9c: Bulk-approve buttons */}
          {session.canApprove && items.some(i => i.status !== 'approved') && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                size="small"
                variant="outlined"
                color="success"
                disabled={bulkApproving}
                onClick={() => handleBulkApprove('no-change')}
                data-testid="bulk-approve-no-change"
              >
                Approve all zero-change
              </Button>
              {compareMode && otherSessions.length > 0 && (
                <Button
                  size="small"
                  variant="outlined"
                  color="success"
                  disabled={bulkApproving}
                  onClick={() => handleBulkApprove('agreed')}
                  data-testid="bulk-approve-agreed"
                >
                  Approve all agreed items
                </Button>
              )}
            </Box>
          )}

          {/* F3: use server-computed canClose — handles 'expired' state that was previously
               mislabelled 'closed' by the UI chip, keeping the button incorrectly hidden */}
          {session.canClose && (
            <Button
              variant="outlined"
              color="warning"
              size="small"
              onClick={() => setCloseDialogOpen(true)}
              data-testid="close-session-btn"
            >
              Stop Counting
            </Button>
          )}
        </Stack>
      </Stack>

      {compareLoading && (
        <Alert severity="info" sx={{ mb: 1 }}>
          Loading compare data…
        </Alert>
      )}

      {/* ── Reconciliation Table ─────────────────────────────────────────── */}
      <TableContainer component={Paper} elevation={1}>
        <Table size="small" data-testid="reconciliation-table">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell sx={{ fontWeight: 700 }}>Item</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Location</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Counted</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>System</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Delta</TableCell>
              {/* deltaPct column hidden on small screens */}
              <TableCell
                align="right"
                sx={{ fontWeight: 700, display: { xs: 'none', md: 'table-cell' } }}
              >
                Δ %
              </TableCell>
              {/* Compare columns — one per other session */}
              {compareMode &&
                otherSessions.map((s) => (
                  <TableCell
                    key={s.sessionId}
                    align="right"
                    sx={{ fontWeight: 700, color: 'primary.main' }}
                    data-testid={`compare-col-header-${s.sessionId}`}
                  >
                    {s.name}
                  </TableCell>
                ))}
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {sortedItems.map((item) => {
              const level = getDeltaLevel(item);
              const icon = DELTA_ICON[level];
              const color = DELTA_COLOR[level];

              return (
                <TableRow
                  key={item.itemNumber}
                  data-testid={`item-row-${item.itemNumber}`}
                  hover
                  sx={needsReview(item, compareItems) && item.status === 'pending'
                    ? { bgcolor: 'rgba(244, 67, 54, 0.06)' } : undefined}
                >
                  {/* Item */}
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {item.description}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      #{item.itemNumber}
                    </Typography>
                  </TableCell>

                  {/* Location */}
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {item.location}
                    </Typography>
                  </TableCell>

                  {/* Counted qty */}
                  <TableCell align="right">
                    <Typography variant="body2">
                      {item.countedQty ?? '—'}
                    </Typography>
                  </TableCell>

                  {/* System qty */}
                  <TableCell align="right">
                    <Typography variant="body2">{item.systemQty}</Typography>
                  </TableCell>

                  {/* Delta with icon (colour-blind accessible) */}
                  <TableCell align="right">
                    <Box
                      sx={{ color, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                      data-testid={`delta-cell-${item.itemNumber}`}
                    >
                      <span aria-hidden="true" data-testid={`delta-icon-${item.itemNumber}`}>
                        {icon}
                      </span>
                      <Typography variant="body2" component="span">
                        {item.delta !== null
                          ? item.delta >= 0
                            ? `+${item.delta}`
                            : String(item.delta)
                          : '—'}
                      </Typography>
                    </Box>
                  </TableCell>

                  {/* Δ% — hidden on small screens */}
                  <TableCell
                    align="right"
                    sx={{ display: { xs: 'none', md: 'table-cell' } }}
                    data-testid={`delta-pct-cell-${item.itemNumber}`}
                  >
                    <Typography variant="body2" sx={{ color }}>
                      {item.deltaPct !== null
                        ? `${item.deltaPct.toFixed(1)}%`
                        : '—'}
                    </Typography>
                  </TableCell>

                  {/* Compare columns */}
                  {compareMode &&
                    otherSessions.map((s) => {
                      const otherList = compareItems.get(s.sessionId) ?? [];
                      const match = otherList.find(
                        (oi) => oi.itemNumber === item.itemNumber,
                      );
                      return (
                        <TableCell
                          key={s.sessionId}
                          align="right"
                          data-testid={`compare-cell-${item.itemNumber}-${s.sessionId}`}
                        >
                          <Typography variant="body2" color="text.secondary">
                            {match?.countedQty ?? '—'}
                          </Typography>
                        </TableCell>
                      );
                    })}

                  {/* Status chip */}
                  <TableCell>
                    {item.status === 'approved' && (
                      <Chip
                        label="✓ Approved"
                        size="small"
                        color="success"
                        variant="outlined"
                        data-testid={`approved-chip-${item.itemNumber}`}
                      />
                    )}
                    {item.status === 'rejected' && (
                      <Chip
                        label="✗ Rejected"
                        size="small"
                        color="error"
                        variant="outlined"
                        data-testid={`rejected-chip-${item.itemNumber}`}
                      />
                    )}
                    {item.status === 'pending' && item.counted && (
                      <Chip
                        label="Pending"
                        size="small"
                        color="warning"
                        variant="outlined"
                        data-testid={`pending-chip-${item.itemNumber}`}
                      />
                    )}
                    {!item.counted && (
                      <Typography
                        variant="caption"
                        color="text.disabled"
                        data-testid={`not-counted-${item.itemNumber}`}
                      >
                        Not counted
                      </Typography>
                    )}
                  </TableCell>

                  {/* Action buttons */}
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      {item.status === 'pending' && item.counted && (
                        <>
                          <Tooltip title="Approve — adjust stock">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => setApproveItem(item)}
                              data-testid={`approve-btn-${item.itemNumber}`}
                              aria-label={`Approve ${item.description}`}
                            >
                              <CheckCircleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Reject — keep current stock">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                setRejectItem(item);
                                setRejectReason('');
                              }}
                              data-testid={`reject-btn-${item.itemNumber}`}
                              aria-label={`Reject ${item.description}`}
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}

                      <Tooltip title="View item history">
                        <IconButton
                          size="small"
                          onClick={() => setHistoryItem(item)}
                          data-testid={`history-btn-${item.itemNumber}`}
                          aria-label={`View history for ${item.description}`}
                        >
                          <HistoryIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ── Approve Confirm Dialog ─────────────────────────────────────────── */}
      <Dialog
        open={!!approveItem}
        onClose={() => !approving && setApproveItem(null)}
        maxWidth="xs"
        fullWidth
        data-testid="approve-confirm-dialog"
      >
        <DialogTitle>Adjust stock?</DialogTitle>
        <DialogContent>
          {approveItem && (
            <Typography data-testid="approve-dialog-body">
              Adjust stock from{' '}
              <strong>{approveItem.systemQty}</strong> →{' '}
              <strong>{approveItem.countedQty}</strong> for{' '}
              <strong>{approveItem.description}</strong>?
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setApproveItem(null)}
            disabled={approving}
            data-testid="approve-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleApprove}
            disabled={approving}
            startIcon={
              approving ? <CircularProgress size={16} color="inherit" /> : undefined
            }
            data-testid="approve-confirm"
          >
            {approving ? 'Approving…' : 'Approve'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Reject Bottom Sheet ────────────────────────────────────────────── */}
      <Drawer
        anchor="bottom"
        open={!!rejectItem}
        onClose={() => !rejecting && setRejectItem(null)}
        data-testid="reject-drawer"
        PaperProps={{
          sx: { borderRadius: '16px 16px 0 0', p: 3, maxWidth: 600, mx: 'auto' },
        }}
      >
        <Typography variant="h6" fontWeight={700} mb={1}>
          Reject count?
        </Typography>
        {rejectItem && (
          <Typography variant="body2" color="text.secondary" mb={2}>
            Reject the count of{' '}
            <strong>{rejectItem.countedQty}</strong> for{' '}
            <strong>{rejectItem.description}</strong>. Stock will not change.
          </Typography>
        )}
        <TextField
          label="Reason (optional)"
          value={rejectReason}
          onChange={(e) => setRejectReason((e.target as HTMLInputElement).value)}
          fullWidth
          multiline
          rows={2}
          size="small"
          disabled={rejecting}
          sx={{ mb: 2 }}
          inputProps={{ 'data-testid': 'reject-reason-input' }}
        />
        <Stack spacing={1}>
          <Button
            variant="contained"
            color="error"
            onClick={handleReject}
            disabled={rejecting}
            fullWidth
            startIcon={
              rejecting ? <CircularProgress size={16} color="inherit" /> : undefined
            }
            data-testid="reject-confirm"
          >
            {rejecting ? 'Rejecting…' : 'Reject count'}
          </Button>
          <Button
            variant="text"
            onClick={() => setRejectItem(null)}
            disabled={rejecting}
            fullWidth
            data-testid="reject-cancel"
          >
            Cancel
          </Button>
        </Stack>
      </Drawer>

      {/* ── Close Session Confirm Dialog ───────────────────────────────────── */}
      <Dialog
        open={closeDialogOpen}
        onClose={() => !closing && setCloseDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        data-testid="close-session-confirm-dialog"
      >
        <DialogTitle>Close {session.name}?</DialogTitle>
        <DialogContent>
          {uncountedItems.length > 0 ? (
            <Alert severity="warning" sx={{ mb: 1 }} data-testid="uncounted-warning">
              {uncountedItems.length} item
              {uncountedItems.length !== 1 ? 's' : ''} have not been counted
              and will be skipped — their stock will not change.
            </Alert>
          ) : (
            <Typography>All items have been counted. Close this session?</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCloseDialogOpen(false)}
            disabled={closing}
            data-testid="close-session-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleCloseSession}
            disabled={closing}
            startIcon={
              closing ? <CircularProgress size={16} color="inherit" /> : undefined
            }
            data-testid="close-session-confirm"
          >
            {closing
              ? 'Closing…'
              : uncountedItems.length > 0
              ? `Close and skip ${uncountedItems.length} item${uncountedItems.length !== 1 ? 's' : ''}`
              : 'Close Session'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Item History Drawer ────────────────────────────────────────────── */}
      {historyItem && (
        <OsItemHistoryDrawer
          open={!!historyItem}
          sessionId={session.sessionId}
          itemNumber={historyItem.itemNumber}
          itemDescription={historyItem.description}
          onClose={() => setHistoryItem(null)}
        />
      )}

      {/* ── Toast ─────────────────────────────────────────────────────────── */}
      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast?.severity ?? 'info'} onClose={() => setToast(null)}>
          {toast?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
